import { suggestExpense } from "./expense-suggestions";
import type { Movement } from "./types";

type Control = HTMLInputElement | HTMLSelectElement;
const savingsIds = ["expense-save", "expense-save-type", "expense-save-custom", "expense-save-manual"];
const suggestedIds = ["expense-category", "expense-payment", "expense-budget", "expense-card-funding", ...savingsIds];

export function bindExpenseSuggestions(form: HTMLFormElement, context: {
  movements: () => Movement[];
  today: () => string;
  isEditing: () => boolean;
  updateControls: () => void;
}): { refresh: () => void; preserveChoices: () => void } {
  const name = form.querySelector<HTMLInputElement>("#expense-merchant")!;
  const status = form.querySelector<HTMLElement>("#expense-suggestion-status")!;
  const message = form.querySelector<HTMLElement>("#expense-suggestion-message")!;
  const toggle = form.querySelector<HTMLButtonElement>("#expense-suggestion-toggle")!;
  const dirty = new Set<string>();
  const applied = new Map<string, string | boolean>();
  let enabled = true;
  const control = (id: string) => form.querySelector<Control>(`#${id}`)!;
  const read = (id: string): string | boolean => id === "expense-save" ? (control(id) as HTMLInputElement).checked : control(id).value;
  const write = (id: string, value: string | boolean): void => {
    if (id === "expense-save") (control(id) as HTMLInputElement).checked = Boolean(value);
    else control(id).value = String(value);
  };
  const apply = (id: string, value: string | boolean): void => {
    if (dirty.has(id)) return;
    if (!applied.has(id)) applied.set(id, read(id));
    write(id, value);
  };
  const clear = (): void => {
    applied.forEach((value, id) => { if (!dirty.has(id)) write(id, value); });
    applied.clear();
  };
  const options = (id: string) => [...(control(id) as HTMLSelectElement).options].map((option) => option.value);
  const names = form.querySelector<HTMLDataListElement>("#expense-history-names");
  if (names) {
    const seen = new Set<string>();
    for (const item of context.movements()) {
      if (seen.size >= 50) break;
      if (!item.merchant || seen.has(item.merchant) || item.kind !== "expense" || item.syncState !== "synced"
        || !suggestExpense(item.merchant, [item], { categories: [], paymentMethods: [], today: context.today() })) continue;
      seen.add(item.merchant);
      const option = document.createElement("option");
      option.value = item.merchant;
      names.append(option);
    }
  }
  const refresh = (): void => {
    status.hidden = context.isEditing();
    if (context.isEditing()) return;
    clear();
    toggle.setAttribute("aria-pressed", String(enabled));
    toggle.textContent = enabled ? "Desactivar sugerencias" : "Usar historial";
    const suggestion = enabled ? suggestExpense(name.value, context.movements(), {
      categories: options("expense-category"), paymentMethods: options("expense-payment"), today: context.today(),
    }) : undefined;
    if (!suggestion) {
      message.textContent = !enabled ? "Captura manual. Tus elecciones se conservan."
        : name.value.trim() ? "Sin una coincidencia exacta. Elige las opciones de esta compra."
          : "Escribe un nombre que hayas usado antes para sugerir sus opciones. Puedes cambiarlas.";
      context.updateControls();
      return;
    }
    if (suggestion.category) apply("expense-category", suggestion.category);
    if (suggestion.paymentMethod) apply("expense-payment", suggestion.paymentMethod);
    if (suggestion.budgetImpact !== undefined) apply("expense-budget", suggestion.budgetImpact ? "yes" : "no");
    // Do not apply another card's funding when the user has chosen a different method.
    if (control("expense-payment").value === suggestion.paymentMethod) {
      apply("expense-card-funding", suggestion.cardFundingMode ?? "");
    }
    if (!dirty.has("expense-save")) {
      apply("expense-save", suggestion.savings?.enabled ?? false);
      if (suggestion.savings?.enabled) apply("expense-save-type", String(suggestion.savings.percent));
    }
    message.textContent = `Referencia: ${suggestion.merchant} · ${suggestion.spentOn}. Revisa las opciones antes de guardar.`
      + (suggestion.savings === undefined ? " El ahorro requiere tu elección." : suggestion.savings.enabled ? ` Ahorro sugerido: ${suggestion.savings.percent} %, equivalente al de esa compra.` : " Sin ahorro por compra en esa referencia.");
    context.updateControls();
  };
  // Manual edits remain authoritative across subsequent name changes and toggles.
  suggestedIds.forEach((id) => {
    const mark = (): void => {
      if (context.isEditing()) return;
      const group = savingsIds.includes(id) ? savingsIds : [id];
      group.forEach((field) => { dirty.add(field); applied.delete(field); });
    };
    control(id).addEventListener("input", mark);
    control(id).addEventListener("change", mark);
  });
  name.addEventListener("input", refresh);
  toggle.addEventListener("click", () => { enabled = !enabled; refresh(); });
  form.addEventListener("reset", () => {
    dirty.clear(); applied.clear(); enabled = true;
    queueMicrotask(refresh);
  });
  refresh();
  return { refresh, preserveChoices: () => {
    // A restored failed command already has deliberate choices; no autofill replaces them.
    applied.clear();
    suggestedIds.forEach((id) => dirty.add(id));
    enabled = false;
    refresh();
  } };
}
